import React, { FunctionComponent, useContext } from "react";
import { RouteComponentProps, withRouter } from "react-router-dom";
import { SearchResultPlan } from "../../@types";
import {
  CONSTANTS as C,
  useTranslate,
  IntlContext,
  createComparePlansUri,
} from "../../helpers";
import { StickyTray } from "../../components";
import { AppContext } from "../../app/store";
import { getPlanName } from "../../helpers/planHelpers";
import {
  AnalyticsActionType,
  Ga4Event,
} from "../../app/contexts/Analytics/types";

interface FooterProps extends RouteComponentProps {
  list: SearchResultPlan[];
  onRemovePlanFromComparison: (planId: number | string) => void;
  plansExceededError: boolean;
  fips: string;
  pageLoading: boolean;
}

const PlanCompareFooter: FunctionComponent<FooterProps> = ({
  fips,
  history,
  list,
  pageLoading,
  plansExceededError,
  onRemovePlanFromComparison,
}) => {
  const t = useTranslate();
  const fm = useContext(IntlContext).formatMessage;
  const { state, dispatch } = useContext(AppContext);

  const goToComparePage = (): void => {
    dispatch({
      type: AnalyticsActionType.SEND_TEALIUM_EVENT,
      settings: {
        event_action: "Find Plans - Available Plans",
        event_label: "Compare Drug Plans",
      },
    });

    dispatch({
      type: AnalyticsActionType.SEND_GA4_EVENT,
      settings: {
        event_name: Ga4Event.COMPARE_PROVIDERS,
        compare_count: list.length,
      },
    });

    const compareURI = createComparePlansUri({ planList: list, fips });
    history.push(compareURI.toString());
  };

  const { minPlansToCompare, maxPlansToCompare } = C.compareFooter;

  return (
    <StickyTray
      id="plan-compare-footer"
      heading={`${list.length} ${
        list.length === 1
          ? t("plan_compare_footer.heading.singular")
          : t("plan_compare_footer.heading.plural")
      }`}
      items={list.map(p => ({
        name: getPlanName(p, state.language),
        id: p.id,
      }))}
      onRemoveItem={onRemovePlanFromComparison}
      pageLoading={pageLoading}
      onSubmit={goToComparePage}
      submitDisabled={list.length < minPlansToCompare}
      submitText={t("plan_compare_footer.compare_button")}
      warningMessage={
        plansExceededError ? t("plan_compare_footer.exceeded_error") : ""
      }
      screenReaderText={fm(
        {
          id: "plan_compare_footer.add_more_info",
        },
        { count: maxPlansToCompare - list.length }
      )}
      mobileOpenButtonText={
        list.length === 1
          ? t("plan_compare_footer.mobile_open.singular")
          : fm(
              {
                id: "plan_compare_footer.mobile_open.plural",
              },
              { number: list.length }
            )
      }
    />
  );
};

export default withRouter(PlanCompareFooter);
